import React, { useMemo, useState } from 'react';
import { Alert, Button, Modal, Select, Space, Statistic, Typography, message } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { epcWbsLibraryService } from '@/services/epcWbsLibrary.service';
import { epcEstimationService } from '@/services/epcEstimation.service';

const { Text } = Typography;

export interface EpcWbsLibraryPlugModalProps {
  open: boolean;
  onClose: () => void;
  estimateId: string;
  revisionId: string;
  clientBoqLineId: string;
  boqLineLabel?: string;
}

export const EpcWbsLibraryPlugModal: React.FC<EpcWbsLibraryPlugModalProps> = ({
  open,
  onClose,
  estimateId,
  revisionId,
  clientBoqLineId,
  boqLineLabel,
}) => {
  const qc = useQueryClient();
  const [libraryId, setLibraryId] = useState<string | undefined>();
  const [packageId, setPackageId] = useState<string | undefined>();
  const [parentWbsNodeId, setParentWbsNodeId] = useState<string | undefined>();

  const librariesQ = useQuery({
    queryKey: ['epc-wbs-libraries'],
    queryFn: () => epcWbsLibraryService.listLibraries(),
    enabled: open,
  });

  const packagesQ = useQuery({
    queryKey: ['epc-wbs-lib-packages', libraryId],
    queryFn: () => epcWbsLibraryService.listPackages(libraryId!),
    enabled: open && !!libraryId,
  });

  const nodesQ = useQuery({
    queryKey: ['epc-wbs-lib-nodes-preview', packageId],
    queryFn: () => epcWbsLibraryService.listPackageNodes(packageId!),
    enabled: open && !!packageId,
  });

  const costsQ = useQuery({
    queryKey: ['epc-wbs-lib-costs-preview', packageId],
    queryFn: () => epcWbsLibraryService.listCostTemplates(packageId!),
    enabled: open && !!packageId,
  });

  const wbsQ = useQuery({
    queryKey: ['epc-est-wbs', estimateId, revisionId],
    queryFn: () => epcEstimationService.listWbs(estimateId, revisionId),
    enabled: open,
  });

  const selectedPkg = useMemo(
    () => (packagesQ.data || []).find((p: any) => String(p.id) === packageId),
    [packagesQ.data, packageId]
  );

  const plugM = useMutation({
    mutationFn: () =>
      epcWbsLibraryService.plugLibraryPackage(estimateId, {
        revisionId,
        clientBoqLineId,
        libraryPackageId: packageId!,
        parentWbsNodeId,
      }),
    onSuccess: (data: any) => {
      message.success(
        `Plugged: ${data?.wbsCreated ?? 0} WBS nodes, ${data?.costLinesCreated ?? 0} cost lines`
      );
      void qc.invalidateQueries({ queryKey: ['epc-est-wbs'] });
      void qc.invalidateQueries({ queryKey: ['epc-est-cost'] });
      void qc.invalidateQueries({ queryKey: ['epc-est-boq-detail'] });
      void qc.invalidateQueries({ queryKey: ['epc-est-boq-coverage'] });
      onClose();
    },
    onError: (e: Error & { response?: { data?: { message?: string } } }) =>
      message.error(e?.response?.data?.message || e.message),
  });

  return (
    <Modal
      title="Plug library package into BOQ line"
      open={open}
      onCancel={onClose}
      onOk={() => plugM.mutate()}
      okText="Plug package"
      okButtonProps={{ disabled: !packageId, loading: plugM.isPending }}
      width={560}
      destroyOnClose
    >
      {boqLineLabel && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message={`Target BOQ line: ${boqLineLabel}`}
        />
      )}
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <div>
          <Text type="secondary">Library</Text>
          <Select
            style={{ width: '100%', marginTop: 4 }}
            placeholder="Select Active library"
            value={libraryId}
            onChange={(v) => {
              setLibraryId(v);
              setPackageId(undefined);
            }}
            options={(librariesQ.data || [])
              .filter((l: any) => String(l.status || '') === 'Active')
              .map((l: any) => ({
                value: String(l.id),
                label: `${l.code} — ${l.name}`,
              }))}
          />
        </div>
        <div>
          <Text type="secondary">Published package</Text>
          <Select
            style={{ width: '100%', marginTop: 4 }}
            placeholder={
              (packagesQ.data || []).some((p: any) => String(p.status) === 'Published')
                ? 'Select published package'
                : 'No published packages in this library'
            }
            value={packageId}
            onChange={setPackageId}
            disabled={!libraryId}
            options={(packagesQ.data || [])
              .filter((p: any) => String(p.status || '') === 'Published')
              .map((p: any) => ({
                value: String(p.id),
                label: `${p.code} · v${p.version_number || 1} — ${p.name}`,
              }))}
          />
          {(packagesQ.data || []).length > 0 &&
          !(packagesQ.data || []).some((p: any) => String(p.status) === 'Published') ? (
            <Alert
              type="warning"
              showIcon
              style={{ marginTop: 8 }}
              message="Publish a package in WBS Libraries before plugging"
            />
          ) : null}
        </div>
        <div>
          <Text type="secondary">Optional parent WBS on estimate</Text>
          <Select
            allowClear
            style={{ width: '100%', marginTop: 4 }}
            placeholder="Root (no parent)"
            value={parentWbsNodeId}
            onChange={setParentWbsNodeId}
            options={(wbsQ.data || []).map((n: any) => ({
              value: String(n.id),
              label: `${n.code} — ${n.name}`,
            }))}
          />
        </div>
        {selectedPkg && (
          <Space size="large">
            <Statistic title="WBS nodes" value={(nodesQ.data || []).length} />
            <Statistic title="Cost templates" value={(costsQ.data || []).length} />
          </Space>
        )}
      </Space>
    </Modal>
  );
};
